export default class History {
	constructor(config) {
		this.config = {
			...config,
			// should not be overwritten as that might unexpectedly change the way this class works
			onChange: [],
		};
		this.state = {
			activeCategories: [],
			search: [],
			hash: '',
		};

		this.disassembleString(decodeURIComponent(location.hash));

		window.addEventListener('hashchange', () => {
			this.config.onChange.forEach(func => func(location.hash));
		}, false);
	}

	/**
	 * Returns the current hash, empty if none is available.
	 * 
	 * @returns {string}
	 */
	getHash() {
		return this.state.hash;
	}

	/**
	 * Replaces location history and sets new hash.
	 *! Beware: This will *not* trigger a hashchange event. This is important to not end up in an infinite loop.
	 * 
	 * @param {string} hash 
	 */
	#replace(hash) {
		history.replaceState({}, '', location.origin + location.pathname + hash);
	}

	/**
	 * Creates a string from the current state that can be used as hash string.
	 * 
	 * @returns {string}
	 */
	#assemble() {
		let hash = '';

		let group = Object.keys(this.state.activeCategories);
		group.forEach(g => {
			hash += `&cats[${g}]=${ this.state.activeCategories[g].join(',') }`;
		});
		if (this.state.search.length > 0) {
			hash = `#query=${ this.state.search.map(s => encodeURIComponent(s)).join(',') }${ hash }`;
		} else {
			hash = `#${ hash.substr(1) }`;
		}

		this.state.hash = hash.length > 1 ? hash : '';
		return this.state.hash;
	}

	/**
	 * Disassembles the given string and sets the internal state accordingly.
	 * 
	 * @param {string} string 
	 */
	disassembleString(string) {
		if (string.length === 0) {
			return;
		}

		// Reset
		this.state = {
			activeCategories: [],
			search: []
		};

		// Disassemble
		const hash = string[0] === '#' ? string.substr(1) : string;
		const parts = hash.split('&');
		parts.forEach(part => {
			const [ key, value ] = part.split('=');
			switch (key) {
				case 'query':
					this.state.search = value.split(',').filter(s => s.length > 0).map(s => decodeURIComponent(s));
				break;

				default:
					if (key.match(/^cats\[(\d+)\]/i)) {
						const catGroup = RegExp.$1;
						this.state.activeCategories[catGroup] = this.state.activeCategories[catGroup] || [];
						let vals = value.split(',').map(n => parseInt(n)).filter(n => !isNaN(n))
						this.state.activeCategories[catGroup] = this.state.activeCategories[catGroup].concat(vals);
					}
				break;
			}
		});

		this.state.hash = string;
	}

	/**
	 * For external use, to check if a string is a valid hash that containts search and/or categories.
	 * This can be used to determine if a hash like "#this-is-an-anchor" is a valid hash, which it is not,
	 * before resetting the hash or filter or location hash.
	 * 
	 * @param	{*}		string	The hash string to check.
	 * @returns	{JSON}			JSON object containing valid options.
	 */
	testHash(string) {
		if (!string.length) {
			return { search: [], categories: {} };
		}

		let hasSearch = '';
		let activeCategories = {};

		const hash = string[0] === '#' ? string.substr(1) : string;
		const parts = hash.split('&');

		parts.forEach(part => {
			const [ key, value ] = part.split('=');
			switch (key) {
				case 'query':
					hasSearch = value.split(',').filter(s => s.length > 0).map(s => decodeURIComponent(s));
				break;

				default:
					if (key.match(/^cats\[(\d+)\]/i)) {
						const catGroup = RegExp.$1;
						activeCategories[catGroup] = activeCategories[catGroup] || [];
						let vals = value.split(',').map(n => parseInt(n)).filter(n => !isNaN(n))
						activeCategories[catGroup] = activeCategories[catGroup].concat(vals);
					}
				break;
			}
		});

		return { search: hasSearch, categories: activeCategories };
	}

	/**
	 * Returns the currently active categories.
	 * 
	 * @returns {Object}
	 */
	getActiveCategories() {
		return this.state.activeCategories;
	}

	/**
	 * Sets the currently active categories.
	 * { groupIndex: [ cat1, cat2 ] }
	 * 
	 * @param {Object} activeCats
	 */
	setActiveCategories(activeCats) {
		this.state.activeCategories = activeCats;

		this.#replace(this.#assemble());
	}

	/**
	 * Returns the currently active search queries.
	 * 
	 * @returns {Array}
	 */
	getSearch() {
		return this.state.search;
	}

	/**
	 * Sets the currently active search string.
	 * 
	 * @param {string|Array}	search
	 */
	setSearch(search) {
		this.state.search = typeof search === 'string' ? search.split(',') : search;

		this.#replace(this.#assemble());
	}

	/**
	 * Sets active categories and search string at once.
	 * 
	 * @param {string|Array} search 
	 * @param {Object} activeCats 
	 */
	setSearchAndCategories(search, activeCats) {
		this.state.search = typeof search === 'string' ? search.split(',') : search;
		this.state.activeCategories = activeCats;

		this.#replace(this.#assemble());
	}

	/**
	 * Clears the search and active categories, resets location hash.
	 */
	reset() {
		this.state = {
			activeCategories: [],
			search: []
		};

		this.#replace('');
	}

	/**
	 * Adds a callback to listen on changes of the hash.
	 * Callbacks get as parameter the new hash as validated by `disassembleString()`.
	 * 	
	 * @param {function} callback	The callback to invoke on change.
	 */
	onChange(callback) {
		this.config.onChange.push(callback);
	}
}
